import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaRupeeSign, FaTimes, FaCheck } from 'react-icons/fa';
import { format, addYears, addHours } from 'date-fns';
import { getFirestore, collection, doc, setDoc, addDoc, getDoc } from "firebase/firestore";
import { auth } from "./utils/firebase";
import axios from "axios";

const DAY_START_HOUR = 9;   // 09:00 AM
const DAY_END_HOUR = 20;    // 08:00 PM (last slot is 8 PM, so hour is 20)

const db = getFirestore();

interface BookingPageProps {
  service: {
    id: string;
    name: string;
    price: string; // e.g., '₹500'
    image: string;
  };
  onClose: () => void;
  onConfirm: () => void;
}

interface Address {
  street: string;
  city: string;
  state: string;
  pincode: string;
  landmark: string;
}

const getAmountPaise = (priceStr: string) => {
  const cleaned = Number(String(priceStr).replace(/[^0-9.]/g, ''));
  return Math.round(cleaned * 100);
};

const BookingPage: React.FC<BookingPageProps> = ({ service, onClose, onConfirm }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [address, setAddress] = useState<Address>({
    street: '',
    city: '',
    state: '',
    pincode: '',
    landmark: ''
  });
  const [paymentMethod, setPaymentMethod] = useState<'upi' | 'card' | 'cash'>('upi');
  const [isBookingConfirmed, setIsBookingConfirmed] = useState(false);

  const minDate = format(new Date(), 'yyyy-MM-dd');
  const maxDate = format(addYears(new Date(), 1), 'yyyy-MM-dd');

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM',
    '05:00 PM', '06:00 PM', '07:00 PM', '08:00 PM'
  ];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  function getAvailableTimeSlots(forDateStr: string) {
    const now = new Date();
    const todayStr = format(now, 'yyyy-MM-dd');
    let allowedSlots: string[] = [];

    for (const slot of timeSlots) {
      const [slotHourRaw, slotMinRaw, period] = slot.split(/[: ]/);
      let slotHour = parseInt(slotHourRaw, 10);
      const slotMin = parseInt(slotMinRaw, 10);
      if (period === "PM" && slotHour !== 12) slotHour += 12;
      if (period === "AM" && slotHour === 12) slotHour = 0;

      if (slotHour < DAY_START_HOUR || slotHour > DAY_END_HOUR) continue;

      if (forDateStr === todayStr) {
        const slotT = new Date(now.getFullYear(), now.getMonth(), now.getDate(), slotHour, slotMin);
        const twoHoursLater = addHours(now, 2);
        if (slotT >= twoHoursLater) allowedSlots.push(slot);
      } else {
        allowedSlots.push(slot);
      }
    }
    return allowedSlots;
  }

  useEffect(() => {
    const now = new Date();
    const todayStr = format(now, "yyyy-MM-dd");
    const todaySlots = getAvailableTimeSlots(todayStr);
    if (todaySlots.length === 0) {
      const tomorrow = addHours(now, 24);
      setSelectedDate(format(tomorrow, "yyyy-MM-dd"));
      setSelectedTime('');
    } else {
      setSelectedDate(todayStr);
      setSelectedTime('');
    }
  }, []);

  const handleAddressChange = (field: keyof Address, value: string) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  // ---- MAIN BOOKING HANDLER ----
  const handleConfirmBooking = async () => {
    try {
      const bookingId = doc(collection(db, "bookings")).id;
      const bookingData = {
        userId: auth.currentUser?.uid,
        name: auth.currentUser?.displayName || "Customer",
        email: auth.currentUser?.email || "",
        phone: auth.currentUser?.phoneNumber || "",
        serviceId: service.id,
        serviceName: service.name,
        servicePrice: service.price,
        date: selectedDate,
        time: selectedTime,
        address: address,
        paymentMethod: paymentMethod,
        status: "pending",
        orderId: bookingId,
        createdAt: new Date()
      };

      // Save the booking document first
      await setDoc(doc(db, "bookings", bookingId), bookingData);

      // --- Fetch accurate user email/phone from Firestore users collection
      let userEmail = auth.currentUser?.email || "";
      let userPhone = auth.currentUser?.phoneNumber || "";
      try {
        if (auth.currentUser?.uid) {
          const userDocRef = doc(db, "users", auth.currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            if (userData.email) userEmail = userData.email;
            if (userData.mobile) userPhone = userData.mobile;
          }
        }
      } catch (err) {
        // fallback
        console.warn("Could not fetch user Firestore profile for payment:", err);
      }

      // --- Payment Logic ---
      if (paymentMethod === "cash") {
        // Add payment record for cash immediately
        await addDoc(collection(db, "payments"), {
          bookingId: bookingId,
          serviceId: service.id,
          serviceName: service.name,
          userId: auth.currentUser?.uid,
          userEmail: userEmail,
          userPhone: userPhone,
          mode: "cash",
          status: "success",
          origin: "customer",
          amount: getAmountPaise(service.price),
          pgTxnId: "",
          createdAt: new Date()
        });

        // UI Success
        setIsBookingConfirmed(true);
        const newNotification = {
          id: Date.now().toString(),
          title: 'Booking Confirmed',
          message: `Your ${service.name} has been confirmed for ${selectedDate} at ${selectedTime}`,
          time: 'Just now',
          read: false
        };
        window.dispatchEvent(new CustomEvent('addNotification', { detail: newNotification }));
        setTimeout(() => { onConfirm(); }, 2000);

      } else if (paymentMethod === "upi" || paymentMethod === "card") {
        // Redirect user to PhonePe (no card input UI for card mode)
        const amountPaise = getAmountPaise(service.price);
        const response = await axios.post("/api/payment/initiate-phonepe", {
          amount: amountPaise,
          userId: auth.currentUser?.uid,
          bookingId: bookingId,
          redirectUrl: "https://thekarigarstop.com/payment-success"
        });
        const redirectUrl = response.data.redirectUrl || response.data.paymentUrl;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          alert("Payment initiation failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error in booking/payment:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  const isStepComplete = (step: number) => {
    switch (step) {
      case 1:
        return selectedDate && selectedTime;
      case 2:
        return address.street && address.city && address.state && address.pincode;
      case 3:
        // Card input is hidden for card mode; only method selection needed
        return !!paymentMethod;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        const slotsForSelectedDay = getAvailableTimeSlots(selectedDate);
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Select Date</label>
              <input
                type="date"
                min={minDate}
                max={maxDate}
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedTime('');
                }}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                You can book services from today up to one year in advance
              </p>
              <div className="bg-yellow-100 text-yellow-800 text-xs rounded-xl mt-2 p-2 border border-yellow-300">
                <b>Note:</b> Booking should be made <span className="font-bold">at least 2 hours prior</span> for daily services, and <span className="font-bold">1 day prior</span> for other services.
              </div>
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Select Time Slot</label>
              <div className="grid grid-cols-3 gap-2">
                {timeSlots.map((time) => {
                  const isAvailable = slotsForSelectedDay.includes(time);
                  return (
                    <motion.button
                      key={time}
                      whileHover={{ scale: isAvailable ? 1.05 : 1 }}
                      whileTap={{ scale: isAvailable ? 0.95 : 1 }}
                      onClick={() => isAvailable && setSelectedTime(time)}
                      className={`p-2 rounded-lg text-sm ${
                        selectedTime === time
                          ? 'bg-blue-500 text-white'
                          : isAvailable
                          ? 'bg-gray-100 hover:bg-gray-200'
                          : 'bg-gray-100 text-gray-300 opacity-50 cursor-not-allowed'
                      }`}
                      disabled={!isAvailable}
                    >
                      {time}
                    </motion.button>
                  );
                })}
              </div>
              {slotsForSelectedDay.length === 0 && (
                <p className="text-xs text-red-600 mt-2">
                  No slots are available for this date. Please select another date.
                </p>
              )}
              {selectedDate === format(new Date(), 'yyyy-MM-dd') && (
                <p className="text-sm text-gray-500 mt-2">
                  Only time slots at least <b>2 hours</b> from now are available for today.
                </p>
              )}
            </div>
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Street Address</label>
              <input
                type="text"
                value={address.street}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                placeholder="Enter your street address"
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">City</label>
                <input
                  type="text"
                  value={address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  placeholder="City"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">State</label>
                <input
                  type="text"
                  value={address.state}
                  onChange={(e) => handleAddressChange('state', e.target.value)}
                  placeholder="State"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">PIN Code</label>
              <input
                type="text"
                value={address.pincode}
                onChange={(e) => handleAddressChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit PIN code"
                maxLength={6}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Landmark (Optional)</label>
              <input
                type="text"
                value={address.landmark}
                onChange={(e) => handleAddressChange('landmark', e.target.value)}
                placeholder="Nearby landmark"
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </motion.div>
        );
      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6 overflow-y-auto max-h-[60vh] pr-2"
          >
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Booking Summary</h3>
              <div className="space-y-2 text-sm">
                <p><FaCalendarAlt className="inline mr-2" />{selectedDate}</p>
                <p><FaClock className="inline mr-2" />{selectedTime}</p>
                <p><FaMapMarkerAlt className="inline mr-2" />{address.street}, {address.city}, {address.state} - {address.pincode}</p>
                <p><FaRupeeSign className="inline mr-2" />{service.price}</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-4">Select Payment Method</h3>
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setPaymentMethod('upi')}
                  className={`w-full p-4 rounded-lg text-left ${
                    paymentMethod === 'upi'
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-white border'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      paymentMethod === 'upi'
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-400'
                    }`} />
                    <span className="ml-3">UPI Payment</span>
                  </div>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setPaymentMethod('card')}
                  className={`w-full p-4 rounded-lg text-left ${
                    paymentMethod === 'card'
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-white border'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      paymentMethod === 'card'
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-400'
                    }`} />
                    <span className="ml-3">Card Payment</span>
                  </div>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setPaymentMethod('cash')}
                  className={`w-full p-4 rounded-lg text-left ${
                    paymentMethod === 'cash'
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-white border'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      paymentMethod === 'cash'
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-400'
                    }`} />
                    <span className="ml-3">Cash on Service</span>
                  </div>
                </motion.button>
              </div>
            </div>
            {/* Card form removed. Card/UPI go to PhonePe only! */}
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 relative max-h-[90vh] overflow-hidden flex flex-col"
      >
        {!isBookingConfirmed ? (
          <>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
            >
              <FaTimes size={24} />
            </motion.button>
            <div className="mb-8">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
                Book {service.name}
              </h2>
              <p className="text-gray-600">Complete your booking in 3 simple steps</p>
            </div>
            <div className="flex justify-between mb-8">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className="flex flex-col items-center"
                  onClick={() => isStepComplete(step - 1) && setCurrentStep(step)}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep === step
                        ? 'bg-blue-500 text-white'
                        : currentStep > step
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step ? <FaCheck /> : step}
                  </div>
                  <span className="text-sm mt-2">
                    {step === 1 ? 'Date & Time' : step === 2 ? 'Address' : 'Payment'}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-1">
              {renderStep()}
            </div>
            <div className="flex justify-between mt-8 pt-4 border-t">
              {currentStep > 1 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                >
                  Back
                </motion.button>
              )}
              {currentStep < 3 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!isStepComplete(currentStep)}
                  className={`px-6 py-3 rounded-lg font-medium ${
                    isStepComplete(currentStep)
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </motion.button>
              )}
              {currentStep === 3 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleConfirmBooking}
                  disabled={!isStepComplete(currentStep)}
                  className={`px-6 py-3 rounded-lg font-medium ${
                    isStepComplete(currentStep)
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Confirm Booking
                </motion.button>
              )}
            </div>
          </>
        ) : (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center py-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-green-500 rounded-full mx-auto mb-6 flex items-center justify-center"
            >
              <FaCheck className="text-white text-3xl" />
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Confirmed! 🎉</h2>
            <p className="text-gray-600">Your service has been scheduled successfully. ✨</p>
            <p className="text-blue-600 mt-2">
              You’ll receive a servicemen confirmation code & all details soon via <b>WhatsApp</b> and <b>Email</b>.
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default BookingPage;
